'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from '../components/SafeLink';

interface NewsItem {
  id: string;
  type: string;
  publication_date: string;
  title: string;
  description: string;
  image: string | null;
}

interface StrapiResponseItem {
  id: string;
  attributes?: {
    type?: string;
    publication_date?: string;
    date?: string;
    title?: string;
    description?: string;
    image?: {
      data?: {
        attributes?: {
          url?: string;
        };
      };
    } | null;
  };
}

export default function NewsPage() {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('Все');

  useEffect(() => {
    async function fetchNews(): Promise<void> {
      try {
        setLoading(true);
        setError(null);

        let url = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/posts?populate=image`;

        if (filterType !== 'Все') {
          url += `&filters[type][$eq]=${encodeURIComponent(filterType)}`;
        }


        const res = await fetch(url, { cache: 'no-store' });
        const newsData = (await res.json()) as { data: StrapiResponseItem[] };

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        if (!Array.isArray(newsData.data)) {
          throw new Error('Invalid data format: expected an array');
        }

        const formattedNews: NewsItem[] = newsData.data.map((item) => {
          const attributes = item.attributes || {};
          const rawImage = attributes.image?.data?.attributes?.url || null;
          const imageUrl = rawImage
            ? rawImage.startsWith('http')
              ? rawImage
              : `${process.env.NEXT_PUBLIC_STRAPI_API_URL}${rawImage}`
            : null;


          return {
            id: item.id,
            type: attributes.type ?? 'Новость',
            publication_date: attributes.publication_date ?? attributes.date ?? '',
            title: attributes.title ?? 'Без названия',
            description: attributes.description ?? '',
            image: imageUrl,
          };
        });

        setNewsItems(formattedNews);
      } catch (e: unknown) {
        console.error('Failed to fetch news:', e);
        setError(e instanceof Error ? e.message : 'Не удалось загрузить новости.');
        setNewsItems([]);
      } finally {
        setLoading(false);
      }
    }

    fetchNews();
  }, [filterType]);

  return (
    <section className="bg-white py-12 md:py-16">
      <div className="max-w-[1900px] mx-auto px-6 md:px-12">
        <h1 className="text-3xl md:text-4xl font-bold text-center text-black mb-8 md:mb-10">
          Все новости
        </h1>

        <div className="flex justify-center mb-8">
          <nav className="flex space-x-4 md:space-x-6 overflow-x-auto whitespace-nowrap pb-2">
            {['Все', 'Новость', 'Событие', 'Инсайт', 'Публикация'].map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`text-sm md:text-xl font-bold transition-colors duration-300 ${filterType === type ? 'text-blue-900' : 'text-blue-900 hover:text-blue-500'
                  }`}
              >
                {type}
              </button>
            ))}
          </nav>
        </div>

        {loading && <p className="text-center text-xl">Загрузка...</p>}
        {error && <p className="text-center text-red-500">{error}</p>}

        {!loading && !error && newsItems.length === 0 ? (
          <p className="text-center text-gray-600">Нет новостей по выбранной категории.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {newsItems.map((item) => (
              <div
                key={item.id}
                className="bg-gray-100 shadow-lg overflow-hidden border border-gray-200 rounded-lg flex flex-col"
              >
                {item.image && (
                  <div className="relative w-full aspect-[4/2.5]">
                    <Image
                      src={item.image}
                      alt={item.title || 'Новость'}
                      fill
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 25vw"
                      className="object-cover"
                    />
                  </div>
                )}
                <div className="p-6 flex flex-col flex-grow">
                  <span className="inline-block text-gray-500 text-sm font-semibold mb-2">{item.type}</span>
                  <div className="text-gray-500 text-sm mb-2">{item.publication_date}</div>
                  <h3 className="text-base md:text-lg font-bold text-black mb-4">{item.title}</h3>
                  <p className="text-gray-700 mb-4 text-sm md:text-base">{item.description}</p>

                  <Link
                    href={`/news/${item.id}`}
                    className="mt-auto inline-block font-bold bg-blue-900 text-white px-6 py-2 rounded-full hover:bg-blue-500 transition"
                  >
                    Читать далее
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
